import * as cfg from "./../../../../src/config";
// In the DEV environment, the roles API is configured to use the QA Keycloak token for authentication,
// as our QA environment is more stable than DEV. This means that when testing in our DEV environment, we need to
// manually copy the QA Keycloak token from the live QA portal and use it here, as our DEV Keycloak token will not work.
// QA and PROD environments will function automatically without manual intervention.

// QA Keycloak endpoint (used in DEV environment for testing)
const qaKeycloakBaseUrl = "https://keycloak-qa.cern.ch/auth";
// QA Keycloak token (used in DEV environment for testing)
// Replace this manually by going to 'https://groups-portal-qa.web.cern.ch' and copying the 'access_token' value from
// the response of the 'https://keycloak-qa.cern.ch/auth/realms/cern/protocol/openid-connect/token' POST request
const qaKeycloakToken = "XXX";
// Determine the environment
const isDevEnv = process.env.NODE_ENV === "development";

// Use the appropriate base URL and token based on the environment
const keycloakBaseUrl = isDevEnv ? qaKeycloakBaseUrl : cfg.keycloakUrl;
const keycloakEndpoint = `${keycloakBaseUrl}${cfg.tokenExchangeEndpoint}`;
const tokenToUse = isDevEnv ? qaKeycloakToken : null;

// Roles API endpoint
const apiEndpoint = cfg.rolesApiUrl;

/**
 * Exchanges a Keycloak token for a roles API token.
 *
 * @param {string} keycloakToken - The Keycloak access token.
 * @returns {Promise<string>} The roles API access token.
 * @throws {Error} If the exchange fails.
 */
export const exchangeTokenForRolesToken = async (keycloakToken) => {
  const response = await fetch(keycloakEndpoint, {
    method: "POST",
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
    body: new URLSearchParams({
      client_id: cfg.keycloakClientId,
      grant_type: cfg.grantType,
      subject_token: tokenToUse || keycloakToken,
      requested_token_type: cfg.subjectTokenType,
      audience: cfg.rolesApiAudience,
    }),
  });
  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const error = new Error(
      errorData?.error_description || `HTTP Error ${response.status}`
    );
    error.status = response.status;
    throw error;
  }
  const exchangeData = await response.json();
  return exchangeData.access_token;
};

/**
 * Makes an API call with automatic token refresh and retry logic.
 *
 * @param {string} url - The API endpoint.
 * @param {object} options - Fetch options (headers, etc).
 * @param {number} [maxRetries=3] - Maximum number of retries on 401 errors.
 * @returns {Promise<any>} The API response content.
 * @throws {Error} If all retries fail or another error occurs.
 */
export const fetchWithTokenRefresh = async (url, options, maxRetries = 3) => {
  let retries = 0;
  while (retries <= maxRetries) {
    try {
      const response = await fetch(url, options); // nosemgrep: nodejs_scan.javascript-ssrf-rule-node_ssrf
      if (response.ok) {
        const responseData = await response.json();
        return responseData.content;
      }
      if (response.status === 401 && retries < maxRetries) {
        retries++;
        console.warn(
          `Authentication error (401): Retrying token refresh... (Attempt ${retries}/${maxRetries})`
        );
        try {
          const newToken = await exchangeTokenForRolesToken(
            options.headers.Authorization.split(" ")[1]
          );
          options.headers.Authorization = `Bearer ${newToken}`;
        } catch (exchangeError) {
          console.warn(
            `Token exchange failed (Attempt ${retries}/${maxRetries}):`,
            exchangeError
          );
        }
        continue;
      }
      const errorData = await response.json().catch(() => null);
      let errorMessage;
      if (response.status === 401) {
        errorMessage = "Unauthorized access. Your session is no longer valid.";
      } else {
        errorMessage = errorData?.message || `HTTP Error ${response.status}`;
      }
      const error = new Error(errorMessage);
      error.status = response.status;
      throw error;
    } catch (error) {
      if (retries >= maxRetries) {
        throw error;
      }
      console.warn(`Fetch attempt ${retries + 1} failed:`, error);
      retries++;
      if (retries >= maxRetries) throw error;
    }
  }
  throw new Error(`Max retries (${maxRetries}) exceeded for API call.`);
};

/**
 * Builds a query string for the API from the given parameters.
 *
 * @param {object} params - Query parameters (size, sort, search, etc).
 * @returns {string} The query string.
 */
export const buildApiQueryString = (params = {}) => {
  const queryParts = [];
  const {
    size,
    sort,
    search,
    filterGreaterThan,
    filterLessThan,
    rawQueryString,
  } = params;

  if (size !== undefined) {
    queryParts.push(`size=${size}`);
  }
  if (sort) {
    queryParts.push(`sort=${encodeURIComponent(sort)}`);
  }

  const allSearchConditions = [];
  if (search && Array.isArray(search)) {
    allSearchConditions.push(...search);
  }
  if (filterGreaterThan !== undefined && filterGreaterThan !== null) {
    allSearchConditions.push({
      field: "id",
      op: ">",
      value: String(filterGreaterThan),
    });
  }
  if (filterLessThan !== undefined && filterLessThan !== null) {
    allSearchConditions.push({
      field: "id",
      op: "<",
      value: String(filterLessThan),
    });
  }

  if (allSearchConditions.length > 0) {
    let searchString = "";
    let currentGroup = [];
    let nextGroupOp = "AND";

    allSearchConditions.forEach((condition, index) => {
      const { field, op = ":", value, groupOp = "AND" } = condition;
      const conditionStr = `${field}${op}${encodeURIComponent(value)}`;

      if (index === 0) {
        currentGroup.push(conditionStr);
        nextGroupOp = groupOp;
      } else {
        if (
          groupOp === "OR" &&
          nextGroupOp === "AND" &&
          currentGroup.length > 0
        ) {
          searchString +=
            (searchString ? ` ${nextGroupOp} ` : "") +
            (currentGroup.length > 1
              ? `(${currentGroup.join(" AND ")})`
              : currentGroup[0]);
          currentGroup = [conditionStr];
        } else if (
          groupOp === "AND" &&
          nextGroupOp === "OR" &&
          currentGroup.length > 0
        ) {
          searchString +=
            (searchString ? ` ${nextGroupOp} ` : "") +
            currentGroup.join(" OR ");
          currentGroup = [conditionStr];
        } else {
          currentGroup.push(conditionStr);
        }
        nextGroupOp = groupOp;
      }
    });

    if (currentGroup.length > 0) {
      const groupConnector =
        allSearchConditions.length > currentGroup.length ? nextGroupOp : "AND";
      const groupStr =
        currentGroup.length > 1
          ? `(${currentGroup.join(
              ` ${
                groupConnector === "OR" && currentGroup.length > 1
                  ? "OR"
                  : "AND"
              } `
            )})`
          : currentGroup[0];
      searchString += (searchString ? ` ${groupConnector} ` : "") + groupStr;
    }

    if (searchString) {
      queryParts.push(`search=${searchString.replace(/\s/g, "+")}`);
    }
  }

  if (rawQueryString) {
    queryParts.push(rawQueryString);
  }

  return queryParts.join("&");
};

/**
 * Fetches roles from the roles API.
 *
 * @param {string} token - The roles API access token.
 * @param {object} [params={}] - Optional query parameters.
 * @returns {Promise<Array>} List of roles.
 */
export const getRoles = async (token, params = {}) => {
  const defaultParams = { size: 1000, sort: "name,asc", ...params };
  const queryString = buildApiQueryString(defaultParams);
  const url = `${apiEndpoint}/roles${queryString ? `?${queryString}` : ""}`;

  return fetchWithTokenRefresh(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Fetches target mappings for a given role.
 *
 * @param {string} token - The roles API access token.
 * @param {string} roleName - The role name.
 * @param {boolean} isTarget1 - True for 1st target, false for 2nd.
 * @param {object} [params={}] - Optional query parameters.
 * @returns {Promise<Array>} List of target mappings.
 */
export const getTargetMappings = async (
  token,
  roleName,
  isTarget1,
  params = {}
) => {
  const sortField = isTarget1 ? "target1Type" : "target2Type";
  const defaultParams = { size: 1000, sort: `${sortField},asc`, ...params };
  const queryString = buildApiQueryString(defaultParams);
  const url = `${apiEndpoint}/roles/${roleName}/target-mappings${
    queryString ? `?${queryString}` : ""
  }`;

  return fetchWithTokenRefresh(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Fetches target IDs for a given target type.
 *
 * @param {string} token - The roles API access token.
 * @param {object} [params={}] - Optional query parameters.
 * @returns {Promise<Array>} List of target IDs.
 */
export const getTargetIds = async (token, params = {}) => {
  const defaultParams = { size: 1000, sort: "id,asc", ...params };
  const queryString = buildApiQueryString(defaultParams);
  const url = `${apiEndpoint}/targets${queryString ? `?${queryString}` : ""}`;

  return fetchWithTokenRefresh(url, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
};

/**
 * Formats API data for use in AntD selects with optionRender.
 *
 * @param {Array} data - The data array.
 * @param {string} labelKey - The property to use for label/value.
 * @param {string|null} descriptionKey - The property to use for description.
 * @returns {Array} Formatted select options.
 */
export const formatDropdownData = (data, labelKey, descriptionKey) => {
  if (!Array.isArray(data)) return [];
  return data.map((item) => ({
    value: item[labelKey],
    label: item[labelKey],
    ...(descriptionKey ? { description: item[descriptionKey] || "" } : {}),
  }));
};

// Criteria IDs that should be excluded from standard group criteria
// These are handled by dedicated interfaces (roles and participations)
export const excludedCriteriaIds = [
  // Role-based criteria
  "role",
  "roles.role_type",
  "roles.entity_id",
  "roles.entity_type",
  "roles.entity2_id",
  // Participation-based criteria
  "participations.experiment",
  "participations.institute",
  "participations.country_code",
  "primary_experiment",
];

// List of standard operators with their display label, and backend value.
export const standardOperators = [
  { label: "is", value: "Eq" },
  { label: "is not", value: "Neq" },
  { label: "is less than", value: "Lt" },
  { label: "is less than or equal to", value: "Le" },
  { label: "is greater than", value: "Gt" },
  { label: "is greater than or equal to", value: "Ge" },
  { label: "contains", value: "Like" },
  { label: "does not contain", value: "NotLike" },
  { label: "is between", value: "Between" },
  { label: "is not between", value: "NotBetween" },
  { label: "is one of", value: "In" },
  { label: "is not one of", value: "NotIn" },
  { label: "has no value", value: "IsNull" },
  { label: "has a value", value: "IsNotNull" },
  { label: "is true", value: "True" },
  { label: "is false", value: "False" },
];

export const roleOperators = [
  { label: "Equals", value: "Eq" },
  { label: "Not Equals", value: "Neq" },
  { label: "Between", value: "Between" },
  { label: "In", value: "In" },
  { label: "Not In", value: "NotIn" },
];

export const participationOperators = [
  // { label: "None", value: "None" }, // TODO: Should I include this? Check other criteria in BE...
  { label: "Equals", value: "Eq" },
  { label: "Not Equals", value: "Neq" },
  { label: "In", value: "In" },
  { label: "Not In", value: "NotIn" },
];

// Mapping of data types (string, number, boolean) to valid operator values.
// This ensures only relevant operators are available for a given data type.
const dataTypeToOperators = {
  string: [
    "Eq",
    "Neq",
    "Like",
    "NotLike",
    "In",
    "NotIn",
    "IsNull",
    "IsNotNull",
  ],
  number: [
    "Eq",
    "Neq",
    "Lt",
    "Le",
    "Gt",
    "Ge",
    "Between",
    "NotBetween",
    "In",
    "NotIn",
    "IsNull",
    "IsNotNull",
  ],
  boolean: ["True", "False", "IsNull", "IsNotNull"],
};

/**
 * Filters operators based on the selected data type.
 *
 * @param {string} dataType - The selected data type (e.g., 'string', 'number', 'boolean').
 * @returns {Array} List of operators valid for the selected data type.
 */
export const getOperatorsForDataType = (dataType) => {
  // Retrieve the list of valid operator values for the data type.
  const validOperators = dataTypeToOperators[dataType] || [];
  // Filter the `standardOperators` to include only those with values in `validOperators`.
  return standardOperators.filter((operator) =>
    validOperators.includes(operator.value)
  );
};
